/**
 * @typedef {object} ImageSource
 * @property {string} srcset
 * @property {string} [media]
 * @property {string} [type]
 * @property {string} [sizes]
 * @property {string} [width]
 * @property {string} [height]
 */

class ImageService {
	static SUPPORTED_FORMATS = ['png', 'jpg', 'jpeg'];
	static FORMAT_REGEX = new RegExp(`\\.(${ImageService.SUPPORTED_FORMATS.join('|')})$`);

	/**
	 * Gets the extension of an image URL/path
	 * @param {string} src - The image URL/path
	 * @returns {string}
	 */
	static getImageExtension(src) {
		const parts = src.split('.');
		return parts.length > 1 ? parts.pop() || '' : '';
	}

	/**
	 * Checks if the image is an SVG
	 * @param {string} src - The image URL/path
	 * @returns {boolean}
	 */
	static isSvgImage(src) {
		return this.getImageExtension(src) === 'svg';
	}

	/**
	 * Creates WebP variants of image sources
	 * @param {ImageSource[]} sources - The image sources
	 * @returns {ImageSource[]}
	 */
	static createWebPSources(sources) {
		return sources.flatMap((source) => {
			if (this.isSvgImage(source.srcset)) return [source];

			return [
				{
					...source,
					srcset: source.srcset.replace(this.FORMAT_REGEX, '.webp'),
					type: 'image/webp'
				},
				source
			];
		});
	}

	/**
	 * Filters sources that match current media queries
	 * @param {ImageSource[]} sources - The image sources
	 * @returns {ImageSource[]}
	 */
	static getMatchingMediaSources(sources) {
		return sources.filter((s) => !s.media || window.matchMedia(s.media).matches);
	}

	/**
	 * Gets WebP sources from a list of sources
	 * @param {ImageSource[]} sources - The image sources
	 * @returns {ImageSource[]}
	 */
	static getWebPSources(sources) {
		return sources.filter((s) => s.type === 'image/webp');
	}

	/**
	 * Gets the last matching source from a list, with WebP preference
	 * @param {ImageSource[]} sources - The image sources
	 * @returns {string}
	 */
	static getLastMatchingSource(sources) {
		const matched = this.getMatchingMediaSources(sources);
		const webp = this.getWebPSources(matched);

		if (webp.length > 0) {
			return webp[webp.length - 1].srcset;
		}

		return matched.length > 0
			? matched[matched.length - 1].srcset
			: sources[sources.length - 1]?.srcset || '';
	}
}

export default ImageService;
